// =============================================================================
// HORUS V2 — Face Engine (browser-side)
// =============================================================================
// Face recognition 100% di browser via face-api.js (FaceNet 128-dim).
// Dipakai sebagai FALLBACK ketika HF Space (ArcFace 512-dim) offline.
// Tidak ada data wajah yang dikirim ke server pihak ketiga.
//
// Embedding 128-dim disimpan di kolom Supabase: penduduk.face_embedding_light
// (terpisah dari face_embedding 512-dim ArcFace HF — embedding space berbeda).
//
// Catatan: face-api.js di-load via dynamic import dari CDN saat fungsi pertama
// dipanggil — jadi tidak menambah bundle size build awal.
// =============================================================================

// face-api.js@0.22.2 dist/ TIDAK punya .esm.* — switch ke @vladmandic/face-api fork
// (maintained 2024+, ESM proper, drop-in API compatible)
const FACEAPI_CDN  = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/dist/face-api.esm.js';
const MODEL_URL    = 'https://justadudewhohacks.github.io/face-api.js/models';

let _faceapi      = null;
let _initialized  = false;
let _initPromise  = null;

// Lazy-load face-api.js dari CDN. Hanya di-load saat dibutuhkan.
async function loadFaceApi() {
  if (_faceapi) return _faceapi;
  _faceapi = await import(/* @vite-ignore */ FACEAPI_CDN);
  return _faceapi;
}

// Inisialisasi face-api.js + load models. Idempotent — aman dipanggil berulang.
export async function initFaceEngine() {
  if (_initialized) return true;
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    try {
      const faceapi = await loadFaceApi();
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      _initialized = true;
      console.log('[FaceEngine] Models loaded ✅');
      return true;
    } catch (err) {
      console.error('[FaceEngine] Gagal load models:', err);
      _initPromise = null;
      return false;
    }
  })();
  return _initPromise;
}

// Encode wajah dari Blob/File → array 128-dim (atau null kalau wajah tidak terdeteksi)
export async function encodeFaceFromBlob(blob) {
  const ok = await initFaceEngine();
  if (!ok) return null;
  try {
    const faceapi   = await loadFaceApi();
    const img       = await faceapi.bufferToImage(blob);
    const detection = await faceapi
      .detectSingleFace(img, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
      .withFaceDescriptor();
    if (!detection) {
      console.warn('[FaceEngine] Wajah tidak terdeteksi di foto');
      return null;
    }
    return Array.from(detection.descriptor);
  } catch (err) {
    console.error('[FaceEngine] encodeFaceFromBlob error:', err);
    return null;
  }
}

// Encode wajah dari canvas frame kamera/video → array 128-dim
export async function encodeFaceFromCanvas(canvas) {
  const ok = await initFaceEngine();
  if (!ok) return null;
  try {
    const faceapi   = await loadFaceApi();
    const detection = await faceapi
      .detectSingleFace(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
      .withFaceDescriptor();
    if (!detection) return null;
    return Array.from(detection.descriptor);
  } catch (err) {
    console.error('[FaceEngine] encodeFaceFromCanvas error:', err);
    return null;
  }
}

// Deteksi semua wajah dari canvas → [{bbox, confidence, embedding}]
export async function detectFacesFromCanvas(canvas) {
  const ok = await initFaceEngine();
  if (!ok) return [];
  try {
    const faceapi    = await loadFaceApi();
    const detections = await faceapi
      .detectAllFaces(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
      .withFaceDescriptors();
    return detections.map(d => ({
      bbox: {
        x:      d.detection.box.x,
        y:      d.detection.box.y,
        width:  d.detection.box.width,
        height: d.detection.box.height,
      },
      confidence: d.detection.score,
      embedding:  Array.from(d.descriptor),
    }));
  } catch (err) {
    console.error('[FaceEngine] detectFacesFromCanvas error:', err);
    return [];
  }
}

// Cosine similarity 128-dim
function cosineSim128(a, b) {
  if (!a || !b || a.length !== b.length) return 0;
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot   += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}

// Match embedding query vs kumpulan reference (semua penduduk).
// `embeddingsRef` adalah array dari penduduk row (punya `face_embedding_light`).
// Threshold default 0.6 — lebih ketat dari ArcFace karena FaceNet kurang diskriminatif.
export function matchFace(embedding, embeddingsRef, threshold = 0.6) {
  if (!embedding || !embeddingsRef?.length) return null;
  let bestMatch = null;
  let bestSim   = 0;
  for (const ref of embeddingsRef) {
    if (!ref.face_embedding_light) continue;
    const emb = Array.isArray(ref.face_embedding_light)
      ? ref.face_embedding_light
      : Object.values(ref.face_embedding_light);
    const sim = cosineSim128(embedding, emb);
    if (sim > bestSim) {
      bestSim   = sim;
      bestMatch = ref;
    }
  }
  if (bestSim < threshold) return null;
  return { ...bestMatch, similarity: bestSim };
}

// Auto-sync: encode semua foto Supabase yang belum punya face_embedding_light.
// Tidak butuh HF — semua di browser. Cocok dijalankan saat HF offline.
// Returns { synced, failed } counts.
export async function syncAllEmbeddings(supabaseClient, onProgress) {
  const ok = await initFaceEngine();
  if (!ok) {
    console.error('[FaceEngine] Models tidak bisa di-load — sync dibatalkan');
    return { synced: 0, failed: 0 };
  }

  const { data: missing, error } = await supabaseClient
    .from('penduduk')
    .select('nik, nama_lengkap, foto_url')
    .is('face_embedding_light', null)
    .not('foto_url', 'is', null);

  if (error) {
    console.error('[FaceEngine] Query error:', error);
    return { synced: 0, failed: 0 };
  }

  if (!missing?.length) {
    console.log('[FaceEngine] Semua penduduk sudah punya embedding_light ✅');
    return { synced: 0, failed: 0 };
  }

  console.log(`[FaceEngine] ${missing.length} penduduk perlu di-encode`);
  let synced = 0, failed = 0;

  for (const p of missing) {
    try {
      onProgress?.({ current: synced + failed + 1, total: missing.length, nama: p.nama_lengkap });
      const res = await fetch(p.foto_url, { signal: AbortSignal.timeout(10000) });
      if (!res.ok) throw new Error('Foto tidak bisa didownload: ' + res.status);
      const blob = await res.blob();
      const embedding = await encodeFaceFromBlob(blob);
      if (!embedding) throw new Error('Wajah tidak terdeteksi di foto');
      const { error: updateErr } = await supabaseClient
        .from('penduduk')
        .update({ face_embedding_light: embedding })
        .eq('nik', p.nik);
      if (updateErr) throw updateErr;
      synced++;
      console.log(`[FaceEngine] ✅ ${p.nama_lengkap} (NIK: ${p.nik})`);
    } catch (err) {
      failed++;
      console.warn(`[FaceEngine] ❌ ${p.nik}: ${err.message}`);
    }
    await new Promise(r => setTimeout(r, 500)); // throttle 2 per detik
  }

  console.log(`[FaceEngine] Selesai: ${synced} berhasil, ${failed} gagal`);
  return { synced, failed };
}
